import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import VoiceChat from '../components/VoiceChat';
import ClientSidebar from '../components/Dashboard/ClientSidebar';
import { userAPI, getFullUrl, dialogsAPI } from '../services/api';
import RatingModal from '../components/Modals/RatingModal';

const VoiceCoach = () => {
  const { coachId } = useParams();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [activeVoiceTab] = useState('live');
  const [dialogId, setDialogId] = useState(null);
  const [isCreatingDialog, setIsCreatingDialog] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [coachRelation, setCoachRelation] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const fetchCoachAndRelation = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getById(coachId);
        setCoach(response.data.user);

        if (user?.role === 'client') {
          const myCoachesRes = await userAPI.getMyCoaches();
          const myCoaches = Array.isArray(myCoachesRes.data) ? myCoachesRes.data : [];
          const relation = myCoaches.find(c => c.id === coachId);
          if (relation) {
            setCoachRelation(relation);
            if (!relation.isApproved || relation.status !== 'active') {
              setIsBlocked(true);
            }
          } else {
            setIsBlocked(true);
          }
        }
      } catch (err) {
        console.error('Error fetching coach:', err);
        setLiveError('Coach not found');
      } finally {
        setLoading(false);
      }
    };
    if (coachId) fetchCoachAndRelation();
  }, [coachId, user]);

  useEffect(() => {
    if (activeVoiceTab === 'live' && !dialogId && !isCreatingDialog && coach && !isBlocked) {
      const createDialog = async () => {
        setIsCreatingDialog(true);
        setLiveError(null);
        try {
          const userId = user?.id || user?.email || 'test-user-123';
          const response = await dialogsAPI.create({ 
            coach_id: coach.chatId || coach.id, 
            user_id: userId, 
            metadata: { source: "voice" } 
          });
          setDialogId(response.data.id);
        } catch (error) { 
          const msg = error.response?.data?.message || error.message;
          if (error.response?.status === 402 || error.response?.data?.error === 'subscription_inactive') {
            setIsBlocked(true);
          }
          setLiveError(msg); 
        } finally { 
          setIsCreatingDialog(false); 
        }
      };
      createDialog();
    }
  }, [activeVoiceTab, dialogId, isCreatingDialog, coach, user, isBlocked]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-main)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--tangerine)]"></div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] group/design-root overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 flex flex-1 justify-center py-5 w-full">
          <div className="layout-content-container flex flex-col lg:flex-row w-full max-w-[1200px] flex-1 gap-8">

            <main className="flex-1 flex flex-col">
              <header className="mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] tracking-tight font-black">
                      Voice Coaching with {coach?.name || 'Coach'}
                    </h1>
                    <p className="text-[var(--text-secondary)] text-sm mt-2">Connect via live call.</p>
                  </div>
                  {dialogId && (
                    <button 
                      onClick={() => setIsRatingModalOpen(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-[var(--off-white)] hover:bg-gray-200 text-[var(--text-secondary)] rounded-2xl font-bold transition-all shadow-sm"
                    >
                      <span className="material-symbols-outlined text-2xl">call_end</span>
                      Finish
                    </button>
                  )}
                </div>
              </header>

              <div className="animate-fade-in flex-1">
                {isBlocked ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center bg-[var(--surface-white)] border border-[var(--border-light)] rounded-[2.5rem] shadow-xl min-h-[400px]">
                    <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center text-tangerine mb-6 animate-pulse border border-orange-500/20">
                      <span className="material-symbols-outlined text-4xl">lock</span>
                    </div>
                    <h3 className="text-2xl font-black text-[var(--text-primary)] dark:text-white mb-3">Subscription Inactive</h3>
                    <p className="text-[var(--text-secondary)] dark:text-gray-300 text-sm max-w-md mb-8 leading-relaxed">
                      {coachRelation?.path === 'A' 
                        ? "Your coach covers your access, but their subscription is currently inactive. Please contact your coach to restore access." 
                        : `Your monthly subscription is inactive. Please pay $${coachRelation?.price || '120'}/month to start communicating with your coach's avatar.`}
                    </p>
                    {coachRelation?.path === 'B' && (
                      <a 
                        href="/user/billing"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-tangerine hover:bg-[#d97700] text-white font-black text-sm uppercase tracking-widest rounded-2xl shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5 active:scale-95 animate-fade-in"
                      >
                        <span className="material-symbols-outlined text-lg">credit_card</span>
                        Go to Billing
                      </a>
                    )}
                  </div>
                ) : isCreatingDialog ? (
                  <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-12 flex flex-col items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--tangerine)] mb-4"></div>
                    <p className="text-[var(--text-secondary)] font-bold">Establishing connection...</p>
                  </div>
                ) : liveError ? (
                  <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] p-8 text-center min-h-[400px] flex flex-col justify-center items-center">
                    <span className="material-symbols-outlined text-5xl text-red-400 mb-4">error</span>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Connection Error</h3>
                    <p className="text-[var(--text-secondary)] mb-8">{liveError}</p>
                    <button onClick={() => { setLiveError(null); setDialogId(null); }} className="bg-[var(--tangerine)] text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-orange-500/20">Try Again</button>
                  </div>
                ) : (
                  <VoiceChat
                    dialogId={dialogId}
                    coachName={coach?.name}
                    coachAvatar={getFullUrl(coach?.avatarUrl)}
                    settingsId={coach?.chatId}
                  />
                )}
              </div>
            </main>

            <ClientSidebar selectedCoach={coach} activeTab="voice" />
          </div>
        </div>
      </div>
      <RatingModal 
        isOpen={isRatingModalOpen} 
        onClose={() => setIsRatingModalOpen(false)} 
        dialogId={dialogId} 
      />
    </div>
  );
};

export default VoiceCoach;
